import {
    forwardRef,
    useImperativeHandle,
    useRef,
    useState,
    useEffect,
} from 'react'

import DataList from '../../../../../../Components/DataList'
import StatusIcon from '../../../../../../Components/Common/StatusIcon'

import ExpandableComponentContext from '../../../../../../Contexts/ExpandableComponentContext'

const EmploymentInput = forwardRef(({ title, arrayOf, data, isSelected, handleFieldSelection, yearsToSearch = 7 }, ref) => {

    const dataListRef = useRef()
    const [isOpen, setIsOpen] = useState(isSelected)
    const [completed, setCompleted] = useState(false)

    const [error, setError] = useState("")

    useEffect(() => {
        setIsOpen(isSelected)
    }, [isSelected])

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {
            const employmentInputResults = dataListRef.current.validateAndGetData()
            const employments = employmentInputResults.value

            const allIntervals = employments.map(employment => {
                return employment?.is_unemployed_and_data?.dateFromTo ||
                    employment?.is_unemployed_and_data.employmentData?.currently_employed_and_contact_consent?.dateFromTo
            })

            allIntervals.sort((a, b) => {
                // Convert to year and month numbers for comparison
                const aYear = Number(a.to.year)
                const aMonth = Number(a.to.month)
                const bYear = Number(b.to.year)
                const bMonth = Number(b.to.month)

                // Compare by year first, then by month
                if (bYear !== aYear) {
                    return bYear - aYear // Descending by year
                } else {
                    return bMonth - aMonth // Descending by month
                }
            })

            let areIntervalsValid = true

            for (let i = 0; i < allIntervals.length - 1; i++) {
                const currentInterval = allIntervals[i]
                const nextInterval = allIntervals[i + 1]

                const currentFromMonths = Number(currentInterval.from.month) + (Number(currentInterval.from.year) * 12)
                const nextToMonths = Number(nextInterval.to.month) + (Number(nextInterval.to.year) * 12)

                if (currentFromMonths > nextToMonths + 1) {
                    areIntervalsValid = false
                    break
                }
            }

            const fromDateList = allIntervals.map(interval => interval?.from)

            let isYearRestrictionValid = fromDateList.some(from => {
                if (!from || !from.month || !from.year) {
                    return false
                }
                const inputDate = new Date(parseInt(from.year), parseInt(from.month) - 1, 1)

                const today = new Date()

                const sevenYearsAgo = new Date()
                sevenYearsAgo.setFullYear(today.getFullYear() - yearsToSearch)

                return inputDate < sevenYearsAgo
            })

            isYearRestrictionValid = true

            let isValid = employmentInputResults.isValid && isYearRestrictionValid && areIntervalsValid

            if (isYearRestrictionValid) {
                if (isValid) {
                    setError("")
                }
                else {
                    if (areIntervalsValid) {
                        setError("You need to fix above errors")
                    }
                    else {
                        setError("Please ensure all time intervals are in the correct order and there are no gaps between them.")
                    }
                }
            } else {
                setError(`You need to provide at least ${yearsToSearch} years employment history.`)
            }

            setCompleted(isValid)
            return {
                isValid,
                value: employmentInputResults.value,
            }
        },
    }))

    const handleOpen = () => {
        if (isOpen) {
            setCompleted(dataListRef.current.validateAndGetData().isValid)
        }
        if (isSelected) {
            setIsOpen(!isOpen)
            return
        }
        handleFieldSelection()
    }

    return (
        <div className="mb-4">

            <button
                onClick={handleOpen}
                className="w-full flex justify-between items-center py-2 px-0"
            >
                <span className="text-lg font-semibold">{title}</span>
                <span className="pr-4 flex items-center gap-1">
                    <StatusIcon isOpen={isOpen} completed={completed} />
                    {isOpen ? '▲' : '▼'}
                </span>
            </button>
            <div className={`${!isOpen ? 'hidden' : ''} pt-2`}>

                <ExpandableComponentContext.Provider value={{
                    isInteracted: isOpen
                }}>
                    <DataList
                        ref={dataListRef}
                        attributes={arrayOf.attributes}
                        data={data || [{}]}
                        title={"Employment"}
                    />
                </ExpandableComponentContext.Provider>

                {error && (
                    <p className="text-red-600 text-sm mt-2">
                        {error}
                    </p>
                )}
            </div>
        </div>
    )
})
export default EmploymentInput
